<template>
	<transition name="modal">
		<div
			v-if="showModal"
			class="modal modal-mask"
			@click.self="onCloseClick"
		>
			<div class="modal-container">
				<div class="modal-top">
					<CloseButton
						class="modal-close"
						:rem-size="2.133"
						@click="closeModal"
					/>
				</div>
				<h2 class="modal-header">
					{{ welcomeText }}
				</h2>
				<div class="modal-body">
					{{ welcomeTextBody }}
				</div>
				<div class="modal-footer">
					<RoundedButton
						class="modal-footer__button modal-footer__button-left"
						theme="extra"
						@click="onCloseClick"
					>
						{{ updateButton }}
					</RoundedButton>
				</div>
			</div>
		</div>
	</transition>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import CloseButton from '@lmt-rpb/Atoms/CloseButton/CloseButton.vue';

const timeoutId = ref();

const showModal = ref(false);

const welcomeText = 'Willkommen zurück!';
const welcomeTextBody = 'Bitte aktualisieren Sie Ihre Suche, da sich die Angebote unter Umständen geändert haben.';
const updateButton = 'Aktualisieren';

const props = withDefaults(
	defineProps<{
		timeout?: number;
	}>(),
	{
		timeout: 30 * 60 * 1000, // "lass 30 min, is ma wuascht" -tom
	}
);

const clearTimer = (): void => {
	clearTimeout(timeoutId.value);
};

const reSetAndStartTimer = (): void => {
	clearTimer();

	timeoutId.value = setTimeout(() => {
		showModal.value = true;
		clearTimer();
	}, props.timeout);
};

const closeModal = (): void => {
	showModal.value = false;
	reSetAndStartTimer();
};

const openModal = (): void => {
	showModal.value = true;
};

const emit = defineEmits(['SmallModal:Close']);

const onCloseClick = () => {
	emit('SmallModal:Close');
};

defineExpose({ closeModal, openModal });
</script>

<style lang="scss" scoped>
.modal-mask {
	position: fixed;
	z-index: 9998;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	padding: 0 0.8rem;
	background-color: $color-black-t5;
	display: flex;
	transition: opacity 0.3s ease;
}

.modal-container {
	position: relative;
	margin: auto;
	max-width: 80rem;
	padding: 0;
	background-color: $color-small-modal-background;
	border-radius: $border-radius-large;
	box-shadow: 0.3rem 0.6rem 1.4rem 0 $color-dark-gray-t2;

	@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
		/*  IE specific rules */
		height: 40rem;
	}
}

.modal-header,
.modal-body,
.modal-footer {
	margin-left: 1.6rem;
	margin-right: 1.6rem;
}

.modal-header {
	font-size: $font-medium-1;
	margin-top: 2.4rem;
	margin-bottom: 0.8rem;
	font-weight: $font-weight-bold;
	padding: 0;
	text-align: left;
}

.modal-body {
	font-family: $font-family;
	font-size: $font-small-2;
	font-weight: $font-weight-regular;
	line-height: $line-height-text-small;
}

.modal-footer {
	display: flex;
	justify-content: center;
	margin-top: 2.4rem;
	margin-bottom: 1.6rem;
}

.modal-top {
	padding: 1.6rem;
	margin-bottom: -5rem;
	display: flex;
	justify-content: end;

	.modal-title {
		color: white;
		flex-grow: 1;
		font-weight: $font-weight-bold;
		font-size: $font-medium-2;
	}

	.modal-close {
		border: none;
		background: none;
		color: $color-small-modal-close-button;
		display: inline-flex;
		align-items: center;

		&:focus,
		&:active {
			outline: none;
		}
	}
}

:deep(button.button) {
	border-radius: $small-modal-button-radius;
	background: $color-small-modal-button !important;
}

.modal .modal-footer__button {
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: $font-small-2;
	padding: 0.8rem 2.4rem;

	&-right {
		margin-left: 1rem;
	}

	&--icon {
		width: $font-small-2;
		height: $font-small-2;
		fill: currentcolor;
		margin-right: 0.7rem;
	}
}

@media (min-width: $breakpoint-small) {
	.modal-container {
		max-width: 73.6rem;
	}

	.modal-header {
		margin-top: 4rem;
		font-size: $font-large-1;
	}

	.modal-body {
		font-size: $font-medium-2;
	}

	.modal-footer {
		margin-top: 3.2rem;
		margin-bottom: 3.2rem;
	}

	.modal-header,
	.modal-body,
	.modal-footer {
		margin-left: 4rem;
		margin-right: 4rem;
	}
}

@media (min-width: $breakpoint-large) {
	.modal-container {
		max-width: 80rem;
	}

	.modal-header {
		font-size: $font-large-1;
		margin-top: 4.4rem;
		margin-bottom: 2.4rem;
	}

	.modal-body {
		font-size: $font-medium-1;
	}

	.modal .modal-footer__button {
		padding: 1.6rem 4rem;
		font-size: $font-small-1;
	}

	.modal-footer {
		margin-top: 5.2rem;
		margin-bottom: 6.2rem;
	}

	.modal-header,
	.modal-body,
	.modal-footer {
		margin-left: 10rem;
		margin-right: 10rem;
	}
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
	.modal-container {
		/*  IE specific rules */
		height: 40rem;
	}
}
</style>
